import { FilterMode } from '@lighthouse/core'
import {
    getAppointField,
    getViewColumns,
    notDataSettingInnerTypes,
    useAtomAction,
    useAtomData,
    USER_DATASOURCE,
    ViewFieldFilterConfigure
} from '@lighthouse/shared'
import React, { useCallback, useDeferredValue, useMemo } from 'react'
import { Controller } from 'react-hook-form'

import {
    availableFilterBlocksAtom,
    blockHighlightAtom,
    lastPageOfStackAtom,
    pagePathParameterFamily,
    pageQueryParameterFamily,
    pageStatesParameterFamily,
    pageVariableSchemasFamily,
    selectedBlockCustomViewAtom
} from '@/atoms/page/state'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'

import { ListItemMenu } from '../../ListItemMenu'
import type { BaseFieldInputSetting } from '../types'

interface PersonConfigProps extends Pick<BaseFieldInputSetting, 'prefix'> {}

const PersonConfig: React.FunctionComponent<PersonConfigProps> = ({ prefix }) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()

    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.pageId || '', [])
    )
    const pageVariables = useAtomData(pageVariableSchemasFamily(pageId))
    const pagePathParameter = useAtomData(pagePathParameterFamily(pageId))
    const pageQueryParameter = useAtomData(pageQueryParameterFamily(pageId))
    const pageStateParameter = useAtomData(pageStatesParameterFamily(pageId))
    const availableFilterBlocksInfo = useDeferredValue(useAtomData(availableFilterBlocksAtom))
    const disabledWithVersion = useIsDisabledWithVersion()
    const userDataSource = useDataSource(appId, envId, USER_DATASOURCE)
    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)

    const dataSourceList = useDataSourceList(appId, envId)

    const { viewOptions: userViewOptions, schema: userSchema = {} } = userDataSource ?? {}

    const customViewDsId = useDeferredValue(
        useAtomData(
            selectedBlockCustomViewAtom,
            useCallback(s => s?.config.pointer, [])
        )
    )

    const handleMouseEnter = useCallback(
        (id: string) => {
            setBlockHighlight([id])
            document.querySelector(`div[data-node-id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },
        [setBlockHighlight]
    )

    const handleMouseLeave = useCallback(() => {
        setBlockHighlight([])
    }, [setBlockHighlight])
    const userColumns = getViewColumns({
        tableProps: userViewOptions?.tableProps ?? [],
        schema: userSchema
    })

    const userPrimaryField = useMemo(() => {
        if (!userDataSource) {
            return userColumns[0]?.fieldId
        }
        const field = getAppointField(userDataSource, 'ID')
        return field?.id || userColumns[0]?.fieldId
    }, [userColumns, userDataSource])

    return (
        <Controller
            name={`${prefix}person.filter`}
            render={({ field: { onChange, value } }) => {
                const filter = value
                const filtersLength = filter?.expression?.conditions?.length || 0
                return (
                    <ListItemMenu
                        width="auto"
                        label="数据筛选"
                        value={Boolean(filtersLength) && `已设${String(filtersLength)}个条件`}
                        // icon={<IconFont type="Filter" color="var(--color-gray-400)" size={16} />}
                        popupContent={
                            <ViewFieldFilterConfigure
                                dataSource={userDataSource}
                                dataSourceList={dataSourceList}
                                pagePathParameter={pagePathParameter}
                                pageQueryParameter={pageQueryParameter}
                                pageVariables={pageVariables}
                                pageStateParameter={pageStateParameter}
                                availableFilterBlocksInfo={availableFilterBlocksInfo}
                                viewType={customViewDsId ? 'custom' : undefined}
                                viewDsId={customViewDsId}
                                disabled={disabledWithVersion}
                                columns={userColumns}
                                paramsMode={FilterMode.CUSTOM}
                                primaryField={userPrimaryField}
                                fieldMode={FilterMode.CUSTOM}
                                filter={filter}
                                noSettingInnerType={notDataSettingInnerTypes}
                                onFilter={onChange}
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                            />
                        }
                    />
                )
            }}
        />
    )
}

export default PersonConfig
